/**
 * Autocomplétion d'adresse via la Base Adresse Nationale (BAN,
 * api-adresse.data.gouv.fr). API officielle française, gratuite, sans
 * clé, couvrant 100 % des adresses françaises (y compris résidences,
 * lieux-dits, abréviations « Rés »/« Av »/« Bd »…).
 *
 * Debounce 250 ms. À la sélection, callback onSelect({ address,
 * postalCode, city }) qui permet au formulaire parent de remplir
 * automatiquement les autres champs.
 */
import { Loader2, MapPin } from 'lucide-react';
import {
  useEffect,
  useRef,
  useState,
  type ComponentProps,
} from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

type BanProperties = {
  label: string;
  housenumber?: string;
  name?: string;
  street?: string;
  postcode?: string;
  city?: string;
  context?: string;
};

type BanFeature = {
  properties: BanProperties;
};

type BanResponse = {
  features: BanFeature[];
};

export type SelectedAddress = {
  address: string;
  postalCode: string;
  city: string;
};

type InputBaseProps = Omit<
  ComponentProps<typeof Input>,
  'value' | 'onChange' | 'onSelect'
>;

export interface AddressAutocompleteProps extends InputBaseProps {
  value: string;
  onValueChange: (value: string) => void;
  onSelect: (selection: SelectedAddress) => void;
}

function pickStreet(props: BanProperties): string {
  const number = props.housenumber ? `${props.housenumber} ` : '';
  const street = props.street ?? props.name ?? '';
  return `${number}${street}`.trim() || props.label.split(',')[0]!.trim();
}

export function AddressAutocomplete({
  value,
  onValueChange,
  onSelect,
  className,
  ...inputProps
}: AddressAutocompleteProps) {
  const [results, setResults] = useState<BanFeature[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Ferme la liste si on clique en dehors
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Debounced fetch
  useEffect(() => {
    const trimmed = value.trim();
    if (trimmed.length < 3) {
      setResults([]);
      setLoading(false);
      return;
    }

    const abort = new AbortController();
    const timeout = window.setTimeout(() => {
      setLoading(true);
      const url = new URL('https://api-adresse.data.gouv.fr/search/');
      url.searchParams.set('q', trimmed);
      url.searchParams.set('limit', '6');
      url.searchParams.set('autocomplete', '1');
      fetch(url.toString(), { signal: abort.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((data: BanResponse) => {
          setResults(data.features ?? []);
          setOpen(true);
        })
        .catch((err: unknown) => {
          if ((err as Error)?.name !== 'AbortError') {
            setResults([]);
          }
        })
        .finally(() => setLoading(false));
    }, 250);

    return () => {
      window.clearTimeout(timeout);
      abort.abort();
    };
  }, [value]);

  function handleSelect(item: BanFeature) {
    const street = pickStreet(item.properties);
    const postalCode = item.properties.postcode ?? '';
    const city = item.properties.city ?? '';
    onValueChange(street);
    onSelect({ address: street, postalCode, city });
    setOpen(false);
    setResults([]);
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Input
          {...inputProps}
          value={value}
          onChange={(e) => {
            onValueChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => results.length > 0 && setOpen(true)}
          className={cn('pr-9', className)}
          autoComplete="off"
        />
        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MapPin className="h-4 w-4" />
          )}
        </div>
      </div>

      {open && results.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
        >
          {results.map((item, idx) => (
            <li key={`${item.properties.label}-${idx}`}>
              <button
                type="button"
                onClick={() => handleSelect(item)}
                className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-bcl-grey/60 focus:bg-bcl-grey/60 focus:outline-none"
              >
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-bcl-blue" />
                <span className="line-clamp-2 text-gray-800">
                  {item.properties.label}
                  {item.properties.context ? (
                    <span className="block text-xs text-gray-500">
                      {item.properties.context}
                    </span>
                  ) : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && !loading && value.trim().length >= 3 && results.length === 0 && (
        <p className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500 shadow-lg">
          Aucune adresse trouvée.
        </p>
      )}
    </div>
  );
}
